import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import BookByDishService from "../../services/BookByDishService";
import EventBus from "../../EventBus";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import { CustomSnackbarTypes } from "./CustomSnackBar";

const bookByDishService = new BookByDishService();

const REASON_MAX_LENGTH = 500;

interface Props {
  open: boolean;
  partyId: number;
  currentVisitCharges: number;
  // Called with true when the charges were updated, so the caller knows to refresh.
  callback: (updated: boolean) => void;
}

// Admin-only edit of the visit charges on a Book by Dish visit. The backend owns all the money
// rules (coupon re-evaluation, order re-routing, refusing a settled booking or a reduction below
// the amount already paid) and its error message is surfaced by the HttpClient interceptor.
const UpdateBookByDishVisitChargesDialog = (props: Props) => {
  const [visitCharges, setVisitCharges] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);

  useEffect(() => {
    if (props.open) {
      setVisitCharges(String(props.currentVisitCharges));
      setReason("");
      setSubmitting(false);
    }
  }, [props.open, props.currentVisitCharges]);

  const numericVisitCharges = Number(visitCharges);
  const isInvalid = visitCharges.trim() === "" || isNaN(numericVisitCharges) || numericVisitCharges < 0;
  const isUnchanged = !isInvalid && numericVisitCharges === props.currentVisitCharges;

  const handleSubmit = () => {
    if (isInvalid) {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Please enter valid visit charges (0 or more).",
        type: CustomSnackbarTypes.ERROR,
      });
      return;
    }
    if (!reason.trim()) {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Please enter a reason for changing the visit charges.",
        type: CustomSnackbarTypes.ERROR,
      });
      return;
    }

    setSubmitting(true);
    bookByDishService
      .updateVisitCharges(props.partyId, { visitCharges: numericVisitCharges, reason: reason.trim() })
      .then(() => {
        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
          message: "Visit charges updated successfully.",
          type: CustomSnackbarTypes.SUCCESS,
        });
        props.callback(true);
      })
      .catch(() => {
        setSubmitting(false);
      });
  };

  return (
    <Dialog onClose={() => props.callback(false)} open={props.open} scroll={"paper"} maxWidth="xl">
      <DialogTitle>
        <Typography fontWeight={"600"}>Update Visit Charges</Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ width: "360px", display: "flex", flexDirection: "column", gap: "16px", mt: "4px" }}>
          <Typography variant="body2" color="text.secondary">
            Current visit charges: ₹{props.currentVisitCharges}
          </Typography>
          <TextField
            label="New Visit Charges (₹)"
            value={visitCharges}
            type={"number"}
            fullWidth
            InputProps={{ inputProps: { min: 0 } }}
            error={!!visitCharges && isInvalid}
            helperText={"Set 0 to waive the visit charges. Totals, coupon and pending orders are recalculated."}
            onChange={(event) => setVisitCharges(event.target.value)}
          />
          <TextField
            label="Reason"
            value={reason}
            fullWidth
            required
            multiline
            minRows={2}
            inputProps={{ maxLength: REASON_MAX_LENGTH }}
            helperText={`${reason.length}/${REASON_MAX_LENGTH}`}
            onChange={(event) => setReason(event.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions style={{ marginTop: "12px" }}>
        <Button variant="contained" color={"error"} onClick={() => props.callback(false)} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting || isInvalid || isUnchanged}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateBookByDishVisitChargesDialog;
